import { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  title: string
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  /** Optional sticky footer content (rendered outside scrollable body) */
  footer?: React.ReactNode
  /** If true, clicking the overlay backdrop closes the modal. Default: false */
  closeOnOverlay?: boolean
}

export default function Modal({ title, isOpen, onClose, children, footer, closeOnOverlay = false }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnOverlay && e.target === overlayRef.current) onClose()
    },
    [onClose, closeOnOverlay]
  )

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{ display: 'flex' }}
    >
      <div className="modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-actions">{footer}</div>}
      </div>
    </div>,
    document.body
  )
}
